import { useState, useRef, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Printer, Save, RotateCcw, Loader2, FileDown, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { format, addDays, parse } from "date-fns";
import { ProductCombobox } from "@/components/ProductCombobox";

// Parse date string that could be dd/MM/yyyy, MM/dd/yyyy, or ISO
const parseDateStr = (str) => {
  if (!str) return null;
  // Try dd/MM/yyyy first (our stored format)
  const ddmm = parse(str, "dd/MM/yyyy", new Date());
  if (!isNaN(ddmm.getTime())) return ddmm;
  // Try MM/dd/yyyy fallback (old format)
  const mmdd = parse(str, "MM/dd/yyyy", new Date());
  if (!isNaN(mmdd.getTime())) return mmdd;
  // Try native Date parse (ISO)
  const native = new Date(str);
  if (!isNaN(native.getTime())) return native;
  return null;
};
import { DatePicker } from "@/components/DatePicker";
import { LabelPreview } from "@/components/LabelPreview";
import { LabelScanner } from "@/components/LabelScanner";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LabelCreator() {
  const [searchParams] = useSearchParams();
  const printRef = useRef(null);

  const [productName, setProductName] = useState("");
  const [weight, setWeight] = useState("");
  const [weightUnit, setWeightUnit] = useState("lb");
  const [batchNumber, setBatchNumber] = useState("");
  const [packDate, setPackDate] = useState(new Date());
  const [expirationDate, setExpirationDate] = useState(addDays(new Date(), 7));
  const [quantity, setQuantity] = useState(1);
  const [saving, setSaving] = useState(false);
  const [savingPdf, setSavingPdf] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templates, setTemplates] = useState([]);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/templates`);
      setTemplates(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  useEffect(() => {
    const reprintId = searchParams.get("reprint");
    if (reprintId) {
      axios.get(`${API}/labels/${reprintId}`).then((res) => {
        const label = res.data;
        setProductName(label.product_name);
        setWeight(label.weight);
        setWeightUnit(label.weight_unit || "lb");
        setBatchNumber(label.batch_number);
        const pd = parseDateStr(label.pack_date);
        if (pd) setPackDate(pd);
        const ed = parseDateStr(label.expiration_date);
        if (ed) setExpirationDate(ed);
        toast.success("Label data loaded for reprinting");
      }).catch(() => toast.error("Failed to load label data"));
    }
  }, [searchParams]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === "p") { e.preventDefault(); handlePrint(); }
        if (e.key === "s") { e.preventDefault(); handleSavePdf(); }
        if (e.key === "b") { e.preventDefault(); generateBatchNumber(); }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  });

  const validateForm = () => {
    if (!productName) { toast.error("Select a product first"); return false; }
    if (!weight) { toast.error("Enter the weight"); return false; }
    if (!batchNumber) { toast.error("Enter or generate a batch number"); return false; }
    return true;
  };

  const generateBatchNumber = () => {
    const d = new Date();
    const batch = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}-${String(d.getHours()).padStart(2, "0")}${String(d.getMinutes()).padStart(2, "0")}`;
    setBatchNumber(batch);
  };

  const getLabelPayload = () => ({
    product_name: productName,
    weight,
    weight_unit: weightUnit,
    batch_number: batchNumber,
    pack_date: format(packDate, "dd/MM/yyyy"),
    expiration_date: format(expirationDate, "dd/MM/yyyy"),
  });

  const handlePrint = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      if (quantity > 1) {
        await axios.post(`${API}/labels/bulk`, { ...getLabelPayload(), quantity });
        toast.success(`${quantity} labels saved`);
      } else {
        await axios.post(`${API}/labels`, getLabelPayload());
        toast.success("Label saved");
      }
      await printLabelViaIframe();
    } catch {
      toast.error("Failed to save label");
    } finally {
      setSaving(false);
    }
  };

  const printLabelViaIframe = async () => {
    const labelEl = document.getElementById("printable-label");
    if (!labelEl) return;

    try {
      // Render label as image for clean printing
      const canvas = await html2canvas(labelEl, {
        scale: 3,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
      });
      const imgDataUrl = canvas.toDataURL("image/png");

      // Create hidden iframe for printing
      const iframe = document.createElement("iframe");
      iframe.style.cssText = "position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;";
      document.body.appendChild(iframe);

      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(`<!DOCTYPE html>
<html><head>
<title>Print Label</title>
<style>
  @page { size: 4in 6in; margin: 0; }
  * { margin: 0; padding: 0; }
  html, body { width: 4in; height: 6in; overflow: hidden; }
  img { width: 4in; height: 6in; display: block; }
</style>
</head><body>
<img src="${imgDataUrl}" />
</body></html>`);
      doc.close();

      // Wait for image to load in iframe, then print
      const img = doc.querySelector("img");
      const doPrint = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        // Clean up after a delay
        setTimeout(() => {
          try { document.body.removeChild(iframe); } catch(e) {}
        }, 2000);
      };

      if (img.complete) {
        doPrint();
      } else {
        img.onload = doPrint;
      }
    } catch (err) {
      console.error("Print error:", err);
      // Fallback to direct print
      window.print();
    }
  };

  const handleSavePdf = async () => {
    const labelEl = document.getElementById("printable-label");
    if (!labelEl) return;
    if (!validateForm()) return;
    setSavingPdf(true);
    try {
      await axios.post(`${API}/labels`, getLabelPayload());

      const canvas = await html2canvas(labelEl, {
        scale: 3,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "in", format: [4, 6] });
      pdf.addImage(imgData, "PNG", 0, 0, 4, 6);
      const filename = `label-${productName.replace(/\s+/g, "-").toLowerCase()}-${batchNumber || "draft"}.pdf`;
      pdf.save(filename);
      toast.success("PDF saved & label recorded");
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error("Failed to generate PDF");
    } finally {
      setSavingPdf(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) { toast.error("Enter a template name"); return; }
    try {
      await axios.post(`${API}/templates`, {
        name: templateName,
        product_name: productName,
        weight,
        weight_unit: weightUnit,
        batch_number: batchNumber,
        expiration_days: 7,
        auto_pack_date: true,
      });
      toast.success("Template saved");
      setTemplateDialogOpen(false);
      setTemplateName("");
      loadTemplates();
    } catch { toast.error("Failed to save template"); }
  };

  const loadTemplate = (template) => {
    setProductName(template.product_name);
    setWeight(template.weight);
    setWeightUnit(template.weight_unit || "lb");
    setBatchNumber(template.batch_number);
    setPackDate(new Date());
    setExpirationDate(addDays(new Date(), template.expiration_days || 7));
    toast.success(`Template "${template.name}" loaded`);
  };

  const handleReset = () => {
    setProductName("");
    setWeight("");
    setWeightUnit("lb");
    setBatchNumber("");
    setPackDate(new Date());
    setExpirationDate(addDays(new Date(), 7));
    setQuantity(1);
  };

  return (
    <div data-testid="label-creator-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1
          className="text-4xl md:text-5xl font-bold tracking-tight uppercase"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          CREATE LABEL
        </h1>
        <p className="text-sm text-muted-foreground tracking-wider uppercase mt-1">
          FILL IN DATA AND PRINT
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* Left Panel - Form */}
        <div className="xl:col-span-2 space-y-4">
          {/* Templates Quick Load */}
          {templates.length > 0 && (
            <Card className="bg-card border border-border/50 rounded-sm p-4">
              <p className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground mb-3">
                QUICK LOAD TEMPLATE
              </p>
              <div className="flex flex-wrap gap-2">
                {templates.map((t) => (
                  <Button
                    key={t.id}
                    data-testid={`template-btn-${t.id}`}
                    variant="secondary"
                    size="sm"
                    onClick={() => loadTemplate(t)}
                    className="text-xs uppercase tracking-wider rounded-sm"
                  >
                    {t.name}
                  </Button>
                ))}
              </div>
            </Card>
          )}

          {/* Scan IBP Label */}
          <LabelScanner
            onScanResult={({ batchNumber: scannedBatch, packDate: scannedPackDate }) => {
              if (scannedBatch) setBatchNumber(scannedBatch);
              if (scannedPackDate) {
                try {
                  const parts = scannedPackDate.split("/");
                  let year = parts[2];
                  if (year && year.length === 2) year = "20" + year;
                  const parsed = new Date(`${parts[0]}/${parts[1]}/${year}`);
                  if (!isNaN(parsed.getTime())) setPackDate(parsed);
                } catch { /* keep current date */ }
              }
            }}
          />

          {/* Product Name */}
          <div className="space-y-2">
            <label className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground">PRODUCT NAME</label>
            <ProductCombobox value={productName} onSelect={setProductName} />
          </div>

          {/* Weight */}
          <div className="space-y-2">
            <label className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground">WEIGHT</label>
            <div className="flex gap-2">
              <Input
                data-testid="weight-input"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                autoComplete="off"
                className="flex-1 h-14 bg-input border-2 border-border focus:border-primary text-lg px-4 font-mono rounded-sm"
              />
              <Select value={weightUnit} onValueChange={setWeightUnit}>
                <SelectTrigger data-testid="weight-unit-select" className="w-24 h-14 bg-input border-2 border-border text-lg font-mono rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lb">LB</SelectItem>
                  <SelectItem value="kg">KG</SelectItem>
                  <SelectItem value="oz">OZ</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Batch Number */}
          <div className="space-y-2">
            <label className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground">BATCH / LOT NUMBER</label>
            <div className="flex gap-2">
              <Input
                data-testid="batch-input"
                placeholder="ENTER BATCH #"
                value={batchNumber}
                onChange={(e) => setBatchNumber(e.target.value)}
                autoComplete="off"
                className="flex-1 h-14 bg-input border-2 border-border focus:border-primary text-lg px-4 font-mono rounded-sm uppercase"
              />
              <Button
                data-testid="generate-batch-btn"
                variant="secondary"
                onClick={generateBatchNumber}
                className="h-14 px-4 rounded-sm border border-border text-xs tracking-wider uppercase"
                title="Ctrl+B"
              >
                AUTO
              </Button>
            </div>
          </div>

          {/* Pack Date */}
          <div className="space-y-2">
            <label className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground">PACK DATE</label>
            <DatePicker date={packDate} onSelect={setPackDate} testId="pack-date" />
          </div>

          {/* Expiration Date */}
          <div className="space-y-2">
            <label className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground">EXPIRATION DATE</label>
            <DatePicker date={expirationDate} onSelect={setExpirationDate} testId="exp-date" />
          </div>

          {/* Quantity for bulk */}
          <div className="space-y-2">
            <label className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground">QUANTITY</label>
            <div className="flex items-center gap-3">
              <Button
                data-testid="qty-decrease-btn"
                variant="secondary"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="h-14 w-14 rounded-sm border border-border text-2xl font-mono"
              >
                -
              </Button>
              <Input
                data-testid="quantity-input"
                type="number"
                min="1"
                max="100"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                className="h-14 w-24 bg-input border-2 border-border text-center text-2xl font-mono rounded-sm"
              />
              <Button
                data-testid="qty-increase-btn"
                variant="secondary"
                onClick={() => setQuantity(Math.min(100, quantity + 1))}
                className="h-14 w-14 rounded-sm border border-border text-2xl font-mono"
              >
                +
              </Button>
              {quantity > 1 && (
                <span className="text-xs text-primary font-bold tracking-wider uppercase ml-2">
                  BULK: {quantity} LABELS
                </span>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-2 pt-3">
            <div className="grid grid-cols-2 gap-2">
              <Button
                data-testid="print-label-btn"
                onClick={handlePrint}
                disabled={saving}
                className="col-span-2 h-16 text-lg font-bold uppercase tracking-wider rounded-sm glow-orange bg-primary text-primary-foreground hover:brightness-110 active:scale-95 transition-all duration-75"
                title="Ctrl+P"
              >
                {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Printer className="w-5 h-5 mr-2" />}
                {quantity > 1 ? `PRINT ${quantity} LABELS` : "PRINT LABEL"}
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Button
                data-testid="save-pdf-btn"
                onClick={handleSavePdf}
                disabled={savingPdf}
                variant="secondary"
                className="h-12 rounded-sm border border-border font-bold uppercase tracking-wider text-xs"
                title="Ctrl+S"
              >
                {savingPdf ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileDown className="w-4 h-4 mr-1.5" />}
                SAVE PDF
              </Button>
              <Button
                data-testid="save-template-btn"
                variant="secondary"
                onClick={() => setTemplateDialogOpen(true)}
                className="h-12 rounded-sm border border-border font-bold uppercase tracking-wider text-xs"
                title="Save as Template"
              >
                <Save className="w-4 h-4 mr-1.5" />
                TEMPLATE
              </Button>
              <Button
                data-testid="reset-form-btn"
                variant="secondary"
                onClick={handleReset}
                className="h-12 rounded-sm border border-border font-bold uppercase tracking-wider text-xs"
                title="Reset Form"
              >
                <RotateCcw className="w-4 h-4 mr-1.5" />
                RESET
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground/50 tracking-wider uppercase text-center">
              PRINT OPENS YOUR DEVICE DIALOG TO SELECT WIRELESS PRINTERS
            </p>
          </div>

          {/* Shortcuts hint */}
          <div className="hidden md:flex items-center gap-4 text-[10px] text-muted-foreground/50 tracking-wider uppercase pt-1">
            <span>CTRL+P PRINT</span>
            <span>CTRL+S PDF</span>
            <span>CTRL+B AUTO BATCH</span>
          </div>
        </div>

        {/* Right Panel - Preview */}
        <div className="xl:col-span-3 flex flex-col items-center">
          <div className="sticky top-8 w-full flex flex-col items-center">
            <p className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground mb-4 text-center">
              LIVE PREVIEW
            </p>
            <LabelPreview
              printRef={printRef}
              data={{ productName, weight, weightUnit, batchNumber, packDate, expirationDate }}
            />
          </div>
        </div>
      </div>

      {/* Save Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-[90vw] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="uppercase tracking-wider" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              SAVE AS TEMPLATE
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-3">
            <label className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground">TEMPLATE NAME</label>
            <Input
              data-testid="template-name-input"
              placeholder="E.g., Ribeye Standard"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              autoComplete="off"
              className="h-12 bg-input border-2 border-border focus:border-primary font-mono rounded-sm"
            />
          </div>
          <DialogFooter>
            <Button
              data-testid="save-template-confirm-btn"
              onClick={handleSaveTemplate}
              className="bg-primary text-primary-foreground rounded-sm uppercase tracking-wider font-bold"
            >
              SAVE TEMPLATE
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
